import { useCallback, useState } from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import { Button, CloseButton, Dialog, HStack, Portal } from '@chakra-ui/react'
import { useTranslation } from '@infraloop/i18n'

/**
 * Terms-of-service viewer. The document itself is served by the backend
 * (`selfManagedSubscriptions.tos_url` feature flag) and rendered in an iframe, matching
 * the legacy dashboard's ToS modal. Two modes:
 * - **Read-only** (no `onAgree`) — opened from the footer's 利用規約 link; freely closable.
 * - **Agreement gate** (`onAgree` given) — cannot be dismissed; the user must agree
 *   (同意して続ける) or log out.
 */
const TermsOfServiceModal = ({
  url,
  onClose,
  onAgree,
}: {
  /** URL of the ToS document to display. */
  url: string
  /** Called when the modal requests to close (read-only mode). */
  onClose?: () => void
  /** Records the agreement; when given the modal becomes an undismissable gate. */
  onAgree?: () => Promise<void>
}) => {
  // Hooks
  const { t } = useTranslation()
  const { logout } = useAuth0()

  // States
  const [isAgreeing, setIsAgreeing] = useState(false)

  // Derived state
  const isGate = Boolean(onAgree)

  /**
   * Record the agreement. On success the parent's derived state unmounts the gate; on
   * failure the mutation has toasted the error and the gate stays up for a retry.
   */
  const handleAgree = useCallback(async () => {
    if (!onAgree) return
    setIsAgreeing(true)
    try {
      await onAgree()
    } catch {
      // Error feedback is handled by the mutation's onError toast.
    } finally {
      setIsAgreeing(false)
    }
  }, [onAgree])

  return (
    <Dialog.Root
      open
      onOpenChange={(details) => !details.open && !isGate && onClose?.()}
      closeOnEscape={!isGate}
      closeOnInteractOutside={!isGate}
      size="xl"
      placement="center"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="16px" height="80svh" maxHeight="1000px">
            <Dialog.Header borderBottom="1px solid" borderColor="gray.100">
              <Dialog.Title flex="1" fontSize="19px" fontWeight="bold" color="gray.800">
                {t('hatsuly.footer.terms')}
              </Dialog.Title>
              {!isGate && (
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              )}
            </Dialog.Header>

            <Dialog.Body py="16px">
              <iframe
                title={t('hatsuly.footer.terms')}
                src={url}
                width="100%"
                height="100%"
                style={{ border: '1px solid var(--chakra-colors-gray-200)', borderRadius: '8px' }}
              />
            </Dialog.Body>

            <Dialog.Footer borderTop="1px solid" borderColor="gray.100" bg="#FBFCFE">
              {isGate ? (
                <HStack gap="10px">
                  <Button colorPalette="blue" loading={isAgreeing} onClick={handleAgree} data-testid="tos-agree">
                    {t('hatsuly.modal.tos.agree')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                    data-testid="tos-logout"
                  >
                    {t('ui.logout')}
                  </Button>
                </HStack>
              ) : (
                <Button colorPalette="blue" onClick={onClose} data-testid="tos-close">
                  {t('ui.close')}
                </Button>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default TermsOfServiceModal
